/**
 * importHandoff.ts — predaja popisa s spletne strani v aplikacijo (T3077)
 * ======================================================================
 *
 * Javna spletna stran pošlje datoteko popisa na strežnik, ta jo šifrirano hrani
 * največ eno uro in vrne enkraten žeton. Spletna stran nato preusmeri na
 * `/register#uvoz=<žeton>`. Žeton je v fragmentu, zato ga strežnik in dnevniki
 * ne vidijo.
 *
 * Uporabnik se lahko registrira ali prijavi tudi v drugem zavihku (npr. po
 * potrditvi e-pošte), zato žeton ne sme živeti samo v naslovu: ob zagonu ga
 * `captureImportHandoff()` prepiše v `localStorage` in fragment pobriše.
 * Prijavljeni del aplikacije nato pokaže pasico (`ImportHandoffBanner`), popis
 * projekta pa datoteko enkrat prevzame (`useImportHandoffClaim`).
 *
 * `localStorage` je lahko nedosegljiv (zasebno okno, blokirani podatki), zato
 * je vsak dostop ovit v try/catch — predaja se takrat tiho izgubi, aplikacija
 * pa deluje naprej.
 */

/** Ključ v `localStorage`. */
export const IMPORT_HANDOFF_STORAGE_KEY = 'bp.uvozPredaja';

/** Ime parametra v fragmentu (`#uvoz=<žeton>`). */
export const IMPORT_HANDOFF_HASH_PARAM = 'uvoz';

/** Parameter poizvedbe, s katerim pasica odpre popis novega projekta. */
export const IMPORT_HANDOFF_QUERY_PARAM = 'uvoz';
export const IMPORT_HANDOFF_QUERY_VALUE = 'predaja';

/** Pot do popisa novega projekta, ki prevzame predano datoteko. */
export const importHandoffProjectPath = (projectId: number): string =>
  `/projects/${projectId}/boq?${IMPORT_HANDOFF_QUERY_PARAM}=${IMPORT_HANDOFF_QUERY_VALUE}`;

/** Strežnik datoteko hrani največ eno uro (pogodba T3077). */
export const IMPORT_HANDOFF_TTL_MS = 60 * 60 * 1000;

/** Oblika žetona — enaka kot jo preverja zaledje. */
const TOKEN_RE = /^[A-Za-z0-9_-]{40,64}$/;

export interface PendingImportHandoff {
  token: string;
  /** Čas poteka v ms od epohe (`Date.now()` ob zajemu + 1 ura). */
  expiresAt: number;
}

export function isValidImportHandoffToken(token: unknown): token is string {
  return typeof token === 'string' && TOKEN_RE.test(token);
}

function readStorage(): string | null {
  try {
    return window.localStorage.getItem(IMPORT_HANDOFF_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStorage(value: string): boolean {
  try {
    window.localStorage.setItem(IMPORT_HANDOFF_STORAGE_KEY, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Dogodek v istem zavihku ob spremembi predaje. Brskalnikov `storage` dogodek
 * pride samo v DRUGE zavihke, pasica pa mora izginiti tudi tam, kjer je popis
 * predajo pravkar prevzel.
 */
const CHANGE_EVENT = 'bp:uvoz-predaja';

function notifyChange(): void {
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // Brez okna (npr. SSR) ni poslušalcev.
  }
}

/**
 * Naroči `onChange` na spremembe predaje — v tem zavihku (zajem, prevzem,
 * zavrženje) in v drugih (`storage`). Vrne odjavo.
 */
export function subscribeImportHandoff(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === IMPORT_HANDOFF_STORAGE_KEY) onChange();
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

/** Pobriše lokalni vnos. Strežnik datoteko izbriše sam po roku. */
export function clearImportHandoff(): void {
  try {
    window.localStorage.removeItem(IMPORT_HANDOFF_STORAGE_KEY);
  } catch {
    // Nedosegljiva shramba — ni česa brisati.
  }
  notifyChange();
}

/**
 * Prebere `#uvoz=<žeton>` iz naslova, ga shrani in fragment odstrani.
 *
 * Kliče se v `main.tsx` PRED nastankom usmerjevalnika: `/register` prijavljenega
 * uporabnika preusmeri na `/dashboard` (`App.tsx`) in s tem fragment izgubi.
 * Vrne `true`, ko je bil žeton zajet. Napačna oblika se zavrže, fragment pa se
 * pobriše vseeno — v naslovu ne sme ostati.
 */
export function captureImportHandoff(): boolean {
  let hash: string;
  try {
    hash = window.location.hash;
  } catch {
    return false;
  }
  if (!hash || hash.length < 2) return false;

  const params = new URLSearchParams(hash.slice(1));
  if (!params.has(IMPORT_HANDOFF_HASH_PARAM)) return false;
  const token = params.get(IMPORT_HANDOFF_HASH_PARAM);

  // Fragment odstranimo, drugi deli fragmenta (če jih je kaj) pa ostanejo.
  params.delete(IMPORT_HANDOFF_HASH_PARAM);
  const rest = params.toString();
  const { pathname, search } = window.location;
  try {
    window.history.replaceState(
      window.history.state,
      '',
      `${pathname}${search}${rest ? `#${rest}` : ''}`,
    );
  } catch {
    // Brez history API naslov ostane, kot je — predaja vseeno velja.
  }

  if (!isValidImportHandoffToken(token)) return false;
  const entry: PendingImportHandoff = {
    token,
    expiresAt: Date.now() + IMPORT_HANDOFF_TTL_MS,
  };
  const stored = writeStorage(JSON.stringify(entry));
  if (stored) notifyChange();
  return stored;
}

/** Čakajoča predaja ali `null`. Potekel ali pokvarjen vnos se pobriše. */
export function getPendingImportHandoff(): PendingImportHandoff | null {
  const raw = readStorage();
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearImportHandoff();
    return null;
  }
  const entry = parsed as Partial<PendingImportHandoff> | null;
  if (
    !entry ||
    !isValidImportHandoffToken(entry.token) ||
    typeof entry.expiresAt !== 'number' ||
    entry.expiresAt <= Date.now()
  ) {
    clearImportHandoff();
    return null;
  }
  return { token: entry.token, expiresAt: entry.expiresAt };
}
